import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Bell, Bot, Check, CheckCheck, Clock, Hand, RefreshCw, Send, Trash2,
  User, MessageSquare,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Dialog, DialogClose, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Skeleton } from './ui/skeleton';
import { NumberTicker } from './ui/number-ticker';
import { cn } from '../lib/utils';
import { anima, aparecer, enFila, latiendo, DURACION, CURVA } from './movimientoWhatsApp';

/**
 * Las conversaciones de WhatsApp, y la caja para tomar el control.
 *
 * QUÉ RESUELVE
 * El bot contesta solo, pero cuando alguien pide hablar con una persona se
 * calla y avisa. Sin esta pantalla, ese aviso no llegaba a ningún lado: la
 * persona quedaba esperando a alguien que nunca se enteró.
 *
 * DECISIONES QUE SE VEN EN LA PANTALLA
 *
 * 1. Se muestra la conversación ENTERA, no solo el pedido de ayuda. Un dueño
 *    que solo recibe "alguien pidió una persona" tiene que adivinar de qué
 *    venían hablando; con los mensajes a la vista contesta sabiendo.
 *
 * 2. La lista va por lo más reciente y por nada más. Las que esperan una
 *    persona se ven —marca roja, y un filtro arriba— pero no se adelantan.
 *    Iban primero y era peor: `necesitaHumano` queda prendido hasta que
 *    alguien conteste, así que un pedido de anteayer se clavaba arriba de
 *    todo y tapaba al que entró recién. Un orden puede significar una sola
 *    cosa; que signifique cuándo pasó.
 *
 * 3. El nombre antes que el teléfono, cuando se sabe. Una lista de números
 *    obliga a abrir cada conversación para saber a quién se le está por
 *    contestar.
 *
 * 4. El asistente y el negocio van los dos a la derecha —para quien mira, los
 *    dos son "nosotros"— pero con colores distintos. Estaban iguales, y no
 *    poder distinguir qué contestó uno de qué contestó la máquina es
 *    exactamente lo que hay que poder distinguir acá.
 *
 * SOBRE LOS ESTILOS
 * Va con Tailwind y los componentes de `ui/`, como el resto de lo nuevo del
 * panel. Ojo con una trampa del proyecto: `.dashboard-container *` apaga
 * `transform` y `animation` con `!important` (Dashboard.css:5872), así que
 * cualquier cosa animada por CSS que se renderice acá adentro no se mueve. Lo
 * que sí sobrevive son las transiciones de color y de opacidad, y todo lo que
 * anime por JavaScript escribiendo texto — por eso el contador de arriba
 * funciona y un `animate-in` de Tailwind sería tinta muerta.
 *
 * La forma del hilo (avatar al costado, autor y hora en una línea arriba de la
 * burbuja, burbujas agrupadas por autor) está tomada de un componente de chat
 * de 21st.dev en vez de inventada acá. Lo que cambia respecto de ese molde es
 * lo que este caso tiene y un chat común no: son TRES autores y no dos, y hay
 * mensajes que no son de nadie.
 *
 * El refresco es por consulta cada pocos segundos y no en vivo por Firestore:
 * el backend está en un plan que se duerme, y una conexión que tiene que
 * quedar abierta contra un servicio que arranca en frío es la base equivocada.
 * Es la misma razón por la que el panel de turnos evita el push del servidor.
 */

const CADA = 6000;  // cada cuánto se vuelve a preguntar, en milisegundos

function haceCuanto(iso) {
  if (!iso) return '';
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutos < 1) return 'recién';
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

function horaDe(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * El día de un mensaje, para cortar el hilo.
 *
 * "Hoy" y "Ayer" antes que la fecha: en atención lo que importa es si esto
 * pasó recién o viene de arrastre, y "18 de agosto" obliga a hacer la cuenta.
 */
function diaDe(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  const mismo = (a, b) => a.toDateString() === b.toDateString();
  if (mismo(d, hoy)) return 'Hoy';
  if (mismo(d, ayer)) return 'Ayer';
  return d.toLocaleDateString('es-AR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

/** La inicial del avatar: del nombre si se sabe, y si no del teléfono. */
function inicial(nombre, telefono) {
  const limpio = (nombre || '').trim();
  if (limpio) return limpio[0].toUpperCase();
  const soloNumeros = String(telefono || '').replace(/\D/g, '');
  return soloNumeros.slice(-2, -1) || '#';
}

/** Cómo se llama esta conversación: el nombre si lo dio, el teléfono si no. */
function comoSeLlama(c) {
  return (c?.nombre || '').trim() || c?.telefono || '';
}

/**
 * Un tono corto, generado en el momento.
 *
 * Sin archivo de audio: uno externo es un pedido más que puede fallar y un
 * archivo que hay que servir. Dos notas descendentes y suaves — un aviso que
 * asusta se termina apagando, y apagado no avisa nada.
 */
function sonar() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const ahora = ctx.currentTime;
    [880, 660].forEach((hz, i) => {
      const osc = ctx.createOscillator();
      const vol = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;
      vol.gain.setValueAtTime(0.0001, ahora + i * 0.16);
      vol.gain.exponentialRampToValueAtTime(0.12, ahora + i * 0.16 + 0.02);
      vol.gain.exponentialRampToValueAtTime(0.0001, ahora + i * 0.16 + 0.15);
      osc.connect(vol).connect(ctx.destination);
      osc.start(ahora + i * 0.16);
      osc.stop(ahora + i * 0.16 + 0.16);
    });
    setTimeout(() => ctx.close(), 600);
  } catch (e) { /* sin audio, queda la notificación y la pantalla */ }
}

/**
 * Los tres autores, y cómo se ve cada uno.
 *
 * `mio` decide de qué lado va: el asistente y el negocio son los dos
 * "nosotros" para quien mira, así que van juntos a la derecha. Lo que los
 * separa es el color de la burbuja y el avatar, no la posición — y tiene que
 * separarlos algo, porque distinguir lo que contestó una persona de lo que
 * contestó la máquina es el punto de esta pantalla.
 */
const AUTORES = {
  cliente: {
    etiqueta: 'Cliente', mio: false, Icono: User,
    burbuja: 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm',
    avatar: 'bg-slate-200 text-slate-600',
  },
  bot: {
    etiqueta: 'Asistente', mio: true, Icono: Bot,
    burbuja: 'bg-slate-100 border border-slate-200 text-slate-700 rounded-br-sm',
    avatar: 'bg-slate-300 text-slate-700',
  },
  negocio: {
    etiqueta: 'Vos', mio: true, Icono: Check,
    burbuja: 'bg-emerald-600 border border-emerald-600 text-white rounded-br-sm',
    avatar: 'bg-emerald-600 text-white',
  },
};

/** Mientras carga se muestra la FORMA de la lista, no un cartel que dice
 *  "Cargando…": el salto de un cartel a cuatro filas se siente como que la
 *  pantalla se rehízo, y esto se refresca cada seis segundos. */
function ListaFantasma() {
  return (
    <div className="space-y-1 p-2" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 p-3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-full max-w-[180px]" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * El corte por día. Se calcula al dibujar y no se guarda: es una decisión de
 * cómo se lee, y depende de qué día es hoy.
 *
 * Es una línea con la fecha encima y no una etiqueta suelta: en un panel ancho
 * una pastilla flotando en el medio no se lee como una división, se lee como
 * un mensaje más que quedó centrado.
 */
function CorteDeDia({ momento }) {
  if (!momento) return null;
  return (
    <div className="relative my-4 flex items-center justify-center" role="separator">
      <span className="absolute inset-x-0 top-1/2 h-px bg-slate-200" aria-hidden="true" />
      <span className="relative rounded-full bg-slate-100 px-3 py-0.5 text-[0.7rem] font-semibold capitalize text-slate-500">
        {diaDe(momento)}
      </span>
    </div>
  );
}

/**
 * En qué anda un mensaje que saliste vos, al lado de la hora.
 *
 * Los tres estados dicen cosas distintas y por eso son tres, no dos:
 *
 *   reloj        se lo estamos pasando al asistente (el POST está en vuelo)
 *   un tilde     el asistente lo aceptó
 *   doble tilde  el asistente lo mandó de verdad, y volvió en el hilo
 *
 * La distinción entre los dos tildes no es decorativa: `POST /responder` NO
 * guarda el mensaje —se lo reenvía al bot y contesta `{ ok: true }`—, así que
 * "el bot lo aceptó" y "el cliente lo recibió" son dos momentos separados que
 * pueden tardar. Un solo tilde para los dos mentiría en el medio.
 *
 * Sólo lo llevan los mensajes del negocio. Los del asistente también salieron,
 * pero de esos no hay nada que confirmar desde acá: ya vinieron confirmados.
 */
function EstadoDeEnvio({ estado }) {
  if (estado === 'enviando') {
    return (
      <Clock
        className="h-3 w-3 shrink-0 text-slate-400"
        aria-label="Enviando"
        role="img"
      />
    );
  }
  if (estado === 'enviado') {
    return (
      <Check
        className="h-3.5 w-3.5 shrink-0 text-slate-400"
        aria-label="Enviado"
        role="img"
      />
    );
  }
  return (
    <CheckCheck
      className="h-3.5 w-3.5 shrink-0 text-emerald-600"
      aria-label="Entregado"
      role="img"
    />
  );
}

/**
 * Un mensaje del hilo.
 *
 * `agrupado` es el mensaje anterior del mismo autor y del mismo día: en ese
 * caso se esconden el avatar y la línea de autor, y queda sólo la burbuja
 * corrida. Sin eso, tres mensajes seguidos de la misma persona repiten tres
 * veces la misma cara y el mismo nombre, que es ruido: en una conversación de
 * atención la mitad de los mensajes vienen encadenados.
 */
function Mensaje({ mensaje, quien, nombreCliente, agrupado, animar }) {
  const gesto = aparecer(10);
  return (
    <motion.div
      {...anima}
      // `initial={false}` apaga la entrada sin apagar nada más. Se usa para la
      // primera tanda de un hilo: si animaran los treinta mensajes de golpe, la
      // conversación se prendería de a pedazos como un escáner —el mismo
      // problema por el que la landing terminó desactivando su animación de
      // entrada— y encima con el scroll yéndose al fondo al mismo tiempo.
      initial={animar ? gesto.initial : false}
      animate={gesto.animate}
      transition={gesto.transition}
      className={cn(
        'flex w-full gap-2.5',
        quien.mio ? 'flex-row-reverse' : 'flex-row',
        agrupado ? 'mt-1' : 'mt-4 first:mt-0',
      )}
    >
      {agrupado ? (
        // El hueco del avatar, para que las burbujas encadenadas sigan
        // alineadas con la primera en vez de correrse contra el borde.
        <div className="w-8 shrink-0" aria-hidden="true" />
      ) : (
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className={cn('text-[0.7rem] font-bold', quien.avatar)}>
            {quien.mio ? <quien.Icono className="h-3.5 w-3.5" /> : nombreCliente}
          </AvatarFallback>
        </Avatar>
      )}

      <div
        className={cn(
          'flex min-w-0 max-w-[min(80%,520px)] flex-col',
          quien.mio ? 'items-end' : 'items-start',
        )}
      >
        {/* Autor y hora en la MISMA línea y arriba de la burbuja. Antes el
            autor iba arriba en mayúsculas y la hora abajo suelta: dos renglones
            de metadatos para una burbuja de un renglón de texto. */}
        {!agrupado && (
          <div className="mb-1 flex items-center gap-2 px-0.5">
            <span className="text-[0.72rem] font-semibold leading-none text-slate-600">
              {quien.etiqueta}
            </span>
            {mensaje.momento && (
              <span className="text-[0.68rem] leading-none text-slate-400">
                {horaDe(mensaje.momento)}
              </span>
            )}
            {/* La marca va con la hora y no adentro de la burbuja: la burbuja
                del negocio es verde llena, y un tilde blanco sobre verde no se
                distingue de un tilde gris sobre verde. Acá los tres estados se
                leen igual de bien. */}
            {mensaje.autor === 'negocio' && <EstadoDeEnvio estado={mensaje.estado} />}
          </div>
        )}

        <p
          className={cn(
            'm-0 whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2',
            'text-[0.86rem] leading-relaxed shadow-sm',
            quien.burbuja,
          )}
        >
          {mensaje.texto}
        </p>

        {/* En los encadenados la hora va abajo y chiquita: la línea de arriba
            no se dibuja, pero saber a qué hora entró cada mensaje sigue
            haciendo falta. */}
        {agrupado && mensaje.momento && (
          <span className="mt-0.5 flex items-center gap-1.5 px-1 text-[0.65rem] leading-none text-slate-400">
            {horaDe(mensaje.momento)}
            {mensaje.autor === 'negocio' && <EstadoDeEnvio estado={mensaje.estado} />}
          </span>
        )}
      </div>
    </motion.div>
  );
}

/** Una fila de la lista de conversaciones. */
function FilaDeConversacion({ c, activa, onAbrir, indice }) {
  const tieneNombre = Boolean((c.nombre || '').trim());
  return (
    <motion.button
      {...anima}
      {...enFila(indice)}
      type="button"
      onClick={() => onAbrir(c.telefono)}
      aria-current={activa ? 'true' : undefined}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border border-transparent px-2.5 py-2.5',
        'text-left transition-colors',
        activa
          ? 'border-slate-200 bg-white shadow-sm'
          : 'hover:border-slate-200 hover:bg-white/70',
      )}
    >
      <span className="relative shrink-0">
        <Avatar className="h-10 w-10">
          <AvatarFallback
            className={cn(
              'text-xs font-bold',
              c.necesitaHumano ? 'bg-red-100 text-red-700' : 'bg-slate-200 text-slate-600',
            )}
          >
            {inicial(c.nombre, c.telefono)}
          </AvatarFallback>
        </Avatar>
        {/* El punto rojo sobre el avatar reemplaza a la barra de color del
            costado. La barra pedía una franja de 4px en cada fila para decir
            algo de una fila cada tantas; el punto se ve igual de rápido y no
            deja una columna de nada a la izquierda de toda la lista. */}
        {c.necesitaHumano && (
          <span
            className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-slate-50 bg-red-500"
            aria-hidden="true"
          />
        )}
        {!c.necesitaHumano && c.enManosHumanas && (
          <span
            className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-slate-50 bg-emerald-500"
            aria-hidden="true"
          />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <strong className="truncate text-[0.9rem] font-semibold text-slate-900">
            {comoSeLlama(c)}
          </strong>
          <span className="shrink-0 text-[0.7rem] text-slate-400">
            {haceCuanto(c.ultimoMomento)}
          </span>
        </span>

        {/* El teléfono debajo del nombre y chiquito: sigue siendo con lo que se
            busca a alguien, pero no es lo primero que hay que leer. */}
        {tieneNombre && (
          <span className="block truncate text-[0.7rem] text-slate-400">
            {c.telefono}
          </span>
        )}

        <span className="mt-1 flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[0.78rem] text-slate-500">
            {c.ultimoAutor === 'cliente' ? '' : '↩ '}{c.ultimoTexto}
          </span>
          {c.sinLeer > 0 && (
            <Badge className="h-5 shrink-0 border-transparent bg-blue-600 px-1.5 text-[0.65rem] font-bold text-white hover:bg-blue-600">
              {c.sinLeer}
            </Badge>
          )}
        </span>

        {(c.necesitaHumano || c.enManosHumanas) && (
          <span className="mt-1.5 flex items-center gap-1.5">
            {c.necesitaHumano && (
              <Badge className="border-transparent bg-red-100 px-1.5 py-0 text-[0.65rem] font-bold uppercase tracking-wide text-red-700 hover:bg-red-100">
                te espera
              </Badge>
            )}
            {!c.necesitaHumano && c.enManosHumanas && (
              <Badge className="border-transparent bg-emerald-100 px-1.5 py-0 text-[0.65rem] font-bold uppercase tracking-wide text-emerald-700 hover:bg-emerald-100">
                la tenés vos
              </Badge>
            )}
          </span>
        )}
      </span>
    </motion.button>
  );
}

const ConversacionesWhatsApp = () => {
  const { currentUser } = useAuth();
  const [conversaciones, setConversaciones] = useState([]);
  const [esperando, setEsperando] = useState(0);
  const [configurado, setConfigurado] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [borrador, setBorrador] = useState('');
  const [enviando, setEnviando] = useState(false);
  // Lo que mandaste y todavía no volvió en el hilo. Vive aparte de `mensajes`
  // porque `traerHilo` pisa esa lista entera con lo que dice el servidor: si el
  // mensaje optimista viviera ahí, el primer refresco lo borraría.
  const [pendientes, setPendientes] = useState([]);
  const [devolviendo, setDevolviendo] = useState(false);
  const [tomando, setTomando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  // El filtro es una VISTA, no un orden. La lista viene siempre por lo más
  // reciente; esto sólo esconde lo que no está esperando.
  const [soloEsperando, setSoloEsperando] = useState(false);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);
  const cajaDeMensajes = useRef(null);
  // Se acaba de abrir un hilo: el próximo dibujo tiene que caer al fondo sí o
  // sí, sin preguntar dónde estaba el scroll.
  const recienAbierta = useRef(false);
  // Si estabas mirando el final del hilo, medido MIENTRAS scrolleás y no
  // después de que llegue el mensaje. Ver el efecto de más abajo.
  const estabaAlFondo = useRef(true);
  // Quiénes ya estaban esperando la última vez que miramos. Sin esto, cada
  // consulta volvería a avisar por las mismas conversaciones y el aviso se
  // vuelve ruido en treinta segundos.
  const yaAvisados = useRef(null);
  // De qué conversación se dibujó ya una tanda de mensajes. Es lo que separa
  // "abrí un hilo y llegaron sus treinta mensajes" —que no tiene que animar
  // nada— de "llegó uno nuevo mientras lo mirabas", que sí. Se compara contra
  // `abierta`, así que al cambiar de conversación vuelve a valer para la nueva.
  const hiloYaDibujado = useRef(null);
  const [avisosPermitidos, setAvisosPermitidos] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted',
  );

  const pedir = useCallback(async (ruta, opciones = {}) => {
    const token = await currentUser.getIdToken();
    return fetch(`${API_URL}${ruta}`, {
      ...opciones,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(opciones.headers || {}),
      },
    });
  }, [currentUser]);

  const abrir = useCallback(async (telefono) => {
    setAbierta(telefono);
    setMensajes([]);
    recienAbierta.current = true;
    // Marcar leído acá y no al enviar: abrir la conversación ya es haberla
    // leído, y dejar el globito hasta que conteste haría que el número no
    // signifique nada.
    await pedir(`/api/whatsapp/conversaciones/${encodeURIComponent(telefono)}/leido`,
      { method: 'POST' });
  }, [pedir]);

  /**
   * Avisa por los que recién empezaron a esperar.
   *
   * En la primera consulta NO avisa: al abrir el panel puede haber tres
   * conversaciones pendientes de hace horas, y tres notificaciones de golpe
   * por cosas viejas es exactamente cómo se aprende a ignorarlas. Solo suena
   * lo que pasa mientras estás mirando.
   */
  const avisarDeLosNuevos = useCallback((lista) => {
    const esperandoAhora = new Set(
      lista.filter((c) => c.necesitaHumano).map((c) => c.telefono),
    );
    const previos = yaAvisados.current;
    yaAvisados.current = esperandoAhora;
    if (previos === null) return;            // primera carga: no avisa

    const nuevos = [...esperandoAhora].filter((tel) => !previos.has(tel));
    if (nuevos.length === 0) return;

    for (const tel of nuevos) {
      const c = lista.find((x) => x.telefono === tel);
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const aviso = new Notification('Alguien quiere hablar con vos', {
          body: `${comoSeLlama(c) || tel}\n${c?.ultimoTexto || ''}`.slice(0, 160),
          tag: `wa-${tel}`,     // una por conversación, no una por consulta
        });
        aviso.onclick = () => { window.focus(); abrir(tel); aviso.close(); };
      }
      sonar();
    }
  }, [abrir]);

  const traerLista = useCallback(async () => {
    try {
      const r = await pedir('/api/whatsapp/conversaciones');
      if (!r.ok) throw new Error('No se pudo leer la lista');
      const data = await r.json();
      const lista = data.conversaciones || [];
      avisarDeLosNuevos(lista);
      setConversaciones(lista);
      setEsperando(data.esperando || 0);
      setConfigurado(data.configurado !== false);
      setError('');
    } catch (e) {
      setError('No pude traer las conversaciones. Reintentando…');
    } finally {
      setCargando(false);
    }
  }, [pedir, avisarDeLosNuevos]);

  const traerHilo = useCallback(async (telefono) => {
    try {
      const r = await pedir(`/api/whatsapp/conversaciones/${encodeURIComponent(telefono)}`);
      if (!r.ok) return;
      const data = await r.json();
      setMensajes(data.mensajes || []);
    } catch (e) { /* el reintento del intervalo lo resuelve */ }
  }, [pedir]);

  // La lista se refresca sola. El hilo abierto también: mientras el dueño
  // escribe, la persona del otro lado puede seguir mandando mensajes.
  useEffect(() => {
    traerLista();
    const id = setInterval(traerLista, CADA);
    return () => clearInterval(id);
  }, [traerLista]);

  useEffect(() => {
    if (!abierta) return undefined;
    traerHilo(abierta);
    const id = setInterval(() => traerHilo(abierta), CADA);
    return () => clearInterval(id);
  }, [abierta, traerHilo]);

  /**
   * El hilo que se dibuja: lo que dice el servidor, más lo tuyo que todavía no
   * llegó. Los pendientes van al final porque son, por definición, lo último.
   */
  const hilo = useMemo(() => {
    const mios = pendientes.filter((p) => p.telefono === abierta);
    return mios.length > 0 ? [...mensajes, ...mios] : mensajes;
  }, [mensajes, pendientes, abierta]);

  /**
   * Un pendiente se da por entregado cuando aparece en el hilo del servidor.
   *
   * Se cuenta cuántos mensajes del negocio con ESE texto había al momento de
   * mandarlo y se espera a que haya uno más. Comparar sólo por texto daría por
   * entregado un "listo" nuevo apenas encuentra el "listo" de ayer, y mandar
   * dos veces lo mismo es de lo más común en atención.
   */
  useEffect(() => {
    if (pendientes.length === 0) return;
    setPendientes((previos) => {
      const siguen = previos.filter((p) => {
        if (p.telefono !== abierta) return true;   // otro hilo: no se juzga acá
        const ahora = mensajes.filter(
          (m) => m.autor === 'negocio' && m.texto === p.texto,
        ).length;
        return ahora <= p.yaHabia;
      });
      // Si no se confirmó ninguno se devuelve el MISMO array: uno nuevo cambia
      // la identidad de `pendientes`, y con eso se recalcula el hilo y vuelve a
      // correr el efecto del scroll sin que haya pasado nada.
      return siguen.length === previos.length ? previos : siguen;
    });
  }, [mensajes, abierta, pendientes.length]);

  // Se mueve la CAJA de mensajes, no la página.
  //
  // Acá había un `scrollIntoView`, que scrollea el documento entero: como el
  // hilo se refresca cada 6 segundos, la página se iba sola para abajo una y
  // otra vez mientras el dueño intentaba leer cualquier otra cosa del panel.
  //
  // Son DOS casos y antes eran uno solo, que es de donde venía el problema:
  // al ABRIR una conversación la caja arranca en cero, "estaba abajo" da
  // falso, y te dejaba leyendo el primer mensaje de un hilo de hace tres
  // días. Nadie abre un chat para ver cómo empezó.
  //
  // Al abrir: al fondo, siempre. Con un mensaje nuevo: sólo si no estabas
  // leyendo más arriba.
  //
  // "Estabas abajo" se calcula MIENTRAS scrolleás (ver `alScrollear`) y no acá
  // adentro, y esa es la diferencia entre que funcione o no. Este efecto corre
  // DESPUÉS de que el mensaje nuevo ya se dibujó, así que medir en este punto
  // mide la caja YA CRECIDA: un mensaje más alto que el margen de 80px empuja
  // la cuenta por encima del umbral y el resultado da "estabas leyendo más
  // arriba" aunque estuvieras mirando el final. O sea que justo los mensajes
  // largos —los que más ganas hay de ver— eran los que no traían el scroll.
  useEffect(() => {
    const caja = cajaDeMensajes.current;
    if (!caja || hilo.length === 0) return;

    if (recienAbierta.current) {
      recienAbierta.current = false;
      estabaAlFondo.current = true;
      // Sin animación y en el mismo dibujo: un scroll suave desde arriba
      // muestra toda la conversación pasando de largo, que es peor que el
      // salto.
      caja.scrollTop = caja.scrollHeight;
      return;
    }
    if (estabaAlFondo.current) caja.scrollTop = caja.scrollHeight;
  }, [hilo]);

  /**
   * Anota si estás mirando el final. Corre en cada scroll tuyo, o sea siempre
   * antes de que llegue el próximo mensaje.
   *
   * Los 80px de margen son para que no haga falta estar pegado al borde: con
   * la rueda es normal quedar a unos pixeles del fondo sin querer.
   */
  const alScrollear = (e) => {
    const caja = e.currentTarget;
    estabaAlFondo.current =
      caja.scrollHeight - caja.scrollTop - caja.clientHeight < 80;
  };

  // Corre DESPUÉS del dibujo, que es lo que hace que la primera tanda se
  // dibuje con `animarNuevos` en falso y la siguiente ya en verdadero.
  useEffect(() => {
    if (mensajes.length > 0) hiloYaDibujado.current = abierta;
  }, [mensajes, abierta]);

  /**
   * Mandar, mostrando el mensaje desde el primer momento.
   *
   * Antes el globo aparecía recién después de `traerHilo`, y encima ese refresco
   * casi nunca lo traía: `POST /responder` no guarda nada, se lo pasa al bot, y
   * el mensaje entra al hilo cuando el bot lo reporta por `/bot/evento`. O sea
   * que escribías, apretabas enviar, el campo se vaciaba y no aparecía nada —lo
   * que se lee como que falló—, y el globo salía solo en algún refresco
   * posterior.
   *
   * Ahora el globo se dibuja al apretar y va cambiando de marca: reloj mientras
   * viaja, un tilde cuando el asistente lo aceptó, doble tilde cuando volvió en
   * el hilo. Nada de eso adivina: cada marca corresponde a un hecho.
   */
  const enviar = async (e) => {
    e.preventDefault();
    const texto = borrador.trim();
    if (!texto || enviando) return;

    const telefono = abierta;
    const id = `pendiente-${Date.now()}`;
    const pendiente = {
      id,
      telefono,
      autor: 'negocio',
      texto,
      momento: new Date().toISOString(),
      estado: 'enviando',
      // Cuántos iguales había ya en el hilo, para saber después cuál es éste.
      yaHabia: mensajes.filter((m) => m.autor === 'negocio' && m.texto === texto).length,
    };

    setEnviando(true);
    setError('');
    setPendientes((previos) => [...previos, pendiente]);
    // El campo se vacía ya: lo escrito no se perdió, está en el globo de arriba.
    setBorrador('');

    try {
      const r = await pedir(
        `/api/whatsapp/conversaciones/${encodeURIComponent(telefono)}/responder`,
        { method: 'POST', body: JSON.stringify({ texto }) },
      );
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo enviar');
      }
      setPendientes((previos) => previos.map(
        (p) => (p.id === id ? { ...p, estado: 'enviado' } : p),
      ));
      await traerHilo(telefono);
      traerLista();
    } catch (e2) {
      // Falló de verdad: se saca el globo y el texto vuelve al campo. Dejarlo
      // con una marca de error obligaría a copiarlo a mano para reintentar, y
      // reescribir lo que uno ya escribió es la peor forma de enterarse de un
      // error.
      setPendientes((previos) => previos.filter((p) => p.id !== id));
      setBorrador(texto);
      setError(e2.message);
    } finally {
      setEnviando(false);
    }
  };

  /**
   * Frenar al asistente ya, sin escribir nada todavía.
   *
   * Antes esto sólo pasaba como efecto de contestar, o sea que para callar al
   * bot había que tener la respuesta lista. El apuro real es el otro: ver que
   * está por decir algo mal y necesitar el freno ANTES de redactar.
   */
  const tomar = async () => {
    setTomando(true);
    setError('');
    try {
      const r = await pedir(
        `/api/whatsapp/conversaciones/${encodeURIComponent(abierta)}/tomar`,
        { method: 'POST' },
      );
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo tomar la conversación');
      }
      await traerHilo(abierta);
      traerLista();
    } catch (e) {
      setError(e.message);
    } finally {
      setTomando(false);
    }
  };

  const devolver = async () => {
    setDevolviendo(true);
    setError('');
    try {
      const r = await pedir(
        `/api/whatsapp/conversaciones/${encodeURIComponent(abierta)}/devolver`,
        { method: 'POST' },
      );
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo devolver');
      }
      await traerHilo(abierta);
      traerLista();
    } catch (e) {
      setError(e.message);
    } finally {
      setDevolviendo(false);
    }
  };

  /**
   * Borra la conversación abierta, entera y para siempre.
   *
   * Probar el bot deja conversaciones de prueba —un número inventado, un
   * "prueba de conexión"— y hasta ahora la única forma de sacarlas era entrar a
   * Firestore a mano. El backend borra los mensajes Y el resumen: con sólo los
   * mensajes la conversación seguiría en la lista, vacía.
   *
   * No hay papelera, así que la confirmación se pide antes (Dialog), y el
   * diálogo es de Radix: va en portal, o sea que se salva de la regla del panel
   * que apaga las animaciones, y se abre y cierra como corresponde.
   */
  const borrar = async () => {
    setBorrando(true);
    setError('');
    try {
      const r = await pedir(
        `/api/whatsapp/conversaciones/${encodeURIComponent(abierta)}`,
        { method: 'DELETE' },
      );
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo borrar');
      }
      // Cerrar el hilo ANTES de refrescar: si se refresca primero, por un
      // instante queda abierta una conversación que ya no está en la lista.
      setAbierta(null);
      setMensajes([]);
      await traerLista();
    } catch (e) {
      setError(e.message);
    } finally {
      setBorrando(false);
    }
  };

  const activa = conversaciones.find((c) => c.telefono === abierta);
  const animarNuevos = hiloYaDibujado.current === abierta;
  // La conversación abierta no se esconde nunca, aunque el filtro la deje
  // afuera: se filtra para encontrar algo, y hacer desaparecer lo que estás
  // leyendo no es filtrar, es perderlo.
  const visibles = soloEsperando
    ? conversaciones.filter((c) => c.necesitaHumano || c.telefono === abierta)
    : conversaciones;

  return (
    <div className="flex h-[min(72vh,720px)] flex-col gap-3">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-[220px] flex-1">
          <h2 className="m-0 text-lg font-bold text-slate-900">Conversaciones</h2>
          {/* En celular esta explicación se lleva tres renglones y el hilo
              se queda con menos de dos mensajes a la vista. Se lee una vez y
              no se vuelve a necesitar, así que en pantalla chica no va: ahí el
              espacio es para la conversación. */}
          <p className="m-0 mt-1 hidden text-sm leading-snug text-slate-500 sm:block">
            El asistente contesta solo. Cuando alguien pide hablar con una
            persona, aparece acá y el asistente se calla hasta que vos contestes.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* El contador es un botón: enterarse de que hay tres esperando y
              después tener que buscarlas a ojo en la lista es la mitad del
              trabajo hecho. Se aprieta y quedan sólo esas. */}
          {/* Envuelto para que ENTRE en vez de aparecer de golpe. El número de
              adentro lo escribe un resorte que va de 0 al valor: con un 1 no
              tiene nada que contar y el salto se ve seco, así que lo que suaviza
              la llegada es el desvanecido de la píldora entera, no el conteo. */}
          {esperando > 0 && (
            <motion.span {...anima} {...aparecer(6)} className="inline-flex">
            <Button
              type="button"
              size="sm"
              onClick={() => setSoloEsperando((v) => !v)}
              aria-pressed={soloEsperando}
              className={cn(
                'h-8 gap-1.5 rounded-full border px-3.5 text-[0.82rem] font-semibold',
                soloEsperando
                  ? 'border-red-700 bg-red-700 text-white hover:bg-red-800'
                  : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
              )}
            >
              <NumberTicker value={esperando} />
              {esperando === 1 ? 'espera' : 'esperan'} respuesta
              {soloEsperando && <span className="opacity-70">×</span>}
            </Button>
            </motion.span>
          )}

          {/* El permiso se pide con un botón y no al cargar la pantalla: un
              navegador que pregunta apenas entrás recibe "bloquear" la mitad
              de las veces, y ahí ya no hay forma de volver a pedirlo. */}
          {!avisosPermitidos && typeof Notification !== 'undefined' && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 text-[0.82rem] font-semibold"
              onClick={async () => {
                const r = await Notification.requestPermission();
                setAvisosPermitidos(r === 'granted');
              }}
            >
              <Bell className="h-3.5 w-3.5" />
              {/* Dos textos y no uno partido: el botón es flex con `gap`, así que
                  "Avisarme" y el resto quedaban como dos hijos separados por ese
                  gap y se leía con un espacio de más en el medio. */}
              <span className="sm:hidden">Avisarme</span>
              <span className="hidden sm:inline">Avisarme cuando pidan una persona</span>
            </Button>
          )}

          {/* Se actualiza sola cada pocos segundos, pero el botón está igual:
              cuando alguien está esperando una respuesta, refrescar a mano es
              lo primero que hace la mano. Negarle ese botón no acelera nada y
              deja la sensación de que la pantalla está congelada. */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-[0.82rem] font-semibold"
            onClick={async () => {
              setRefrescando(true);
              await traerLista();
              if (abierta) await traerHilo(abierta);
              setRefrescando(false);
            }}
            disabled={refrescando}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refrescando && 'opacity-50')} />
            {refrescando ? 'Actualizando…' : 'Actualizar'}
          </Button>
        </div>
      </header>

      {error && (
        <p className="m-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="flex min-h-0 flex-1 gap-3">
        {/* En celular se ve una cosa por vez: la lista, o la conversación.
            Dos columnas de 160px cada una no son dos columnas, son ninguna. */}
        <Card
          className={cn(
            'min-h-0 w-full shrink-0 overflow-hidden bg-slate-50',
            'md:block md:w-[300px] lg:w-[340px]',
            abierta ? 'hidden' : 'block',
          )}
        >
          {/* Un div con scroll propio y NO un `ScrollArea` de Radix, por la
              misma razón que la caja de mensajes de al lado aunque el motivo
              sea otro: el viewport de Radix dimensiona por CONTENIDO, así que
              el `w-full` de las filas no las limita a nada y el adelanto del
              último mensaje —que va en una sola línea, con `nowrap`— estiraba
              la fila hasta 468px dentro de una columna de 340. Se veía como si
              `truncate` no anduviera; anda, lo que fallaba era el ancho de la
              caja, y el recorte lo terminaba haciendo el `overflow-hidden` de
              la tarjeta, sin puntos suspensivos y cortando a mitad de palabra. */}
          <div className="h-full overflow-y-auto">
            {cargando && <ListaFantasma />}

            {/* Vacío por falta de conversaciones y vacío por falta de conexión
                se ven igual, y no son lo mismo: en el segundo caso los mensajes
                están llegando al asistente y no se están registrando acá. */}
            {!cargando && conversaciones.length === 0 && !configurado && (
              <p className="m-0 p-5 text-sm leading-relaxed text-amber-800">
                El asistente todavía no está conectado a este panel, así que las
                conversaciones no se registran. Falta configurarlo en el servidor
                (<code className="rounded bg-amber-100 px-1">WHATSAPP_BOT_SECRET</code>{' '}
                y <code className="rounded bg-amber-100 px-1">WHATSAPP_BOT_URL</code>).
              </p>
            )}

            {!cargando && conversaciones.length === 0 && configurado && (
              <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <MessageSquare className="h-6 w-6 text-slate-400" />
                </span>
                <p className="m-0 mt-1 text-sm font-semibold text-slate-600">
                  Todavía no te escribió nadie
                </p>
                <p className="m-0 text-xs leading-relaxed text-slate-400">
                  Cuando alguien le escriba al asistente, la conversación va a
                  aparecer acá.
                </p>
              </div>
            )}

            {soloEsperando && visibles.length === 0 && (
              <p className="m-0 p-5 text-center text-sm text-slate-500">
                Ninguna está esperando respuesta.
              </p>
            )}

            <div className="space-y-0.5 p-2">
              {visibles.map((c, i) => (
                <FilaDeConversacion
                  key={c.telefono}
                  c={c}
                  activa={c.telefono === abierta}
                  onAbrir={abrir}
                  indice={i}
                />
              ))}
            </div>
          </div>
        </Card>

        <Card
          className={cn(
            'min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white',
            abierta ? 'flex' : 'hidden md:flex',
          )}
        >
          {!abierta && (
            <motion.div
              {...anima}
              {...aparecer(6)}
              className="m-auto flex flex-col items-center gap-2 px-6 text-center"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50">
                <MessageSquare className="h-6 w-6 text-slate-300" />
              </span>
              <p className="m-0 mt-1 text-sm text-slate-400">
                Elegí una conversación para verla.
              </p>
            </motion.div>
          )}

          {/* `key={abierta}` es lo que hace que esto ENTRE cada vez que abrís
              otra conversación: sin la key React reusaría el mismo nodo y no
              habría nada que animar. El remonte no toca el scroll —el efecto
              que lo baja depende de `mensajes`, que llegan después— ni pierde
              lo escrito, porque el borrador vive en el estado del componente
              de afuera y no en el textarea. */}
          {abierta && (
            <motion.div
              key={abierta}
              {...anima}
              {...aparecer(8)}
              transition={{ duration: DURACION.panel, ease: CURVA }}
              className="flex min-h-0 min-w-0 flex-1 flex-col"
            >
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setAbierta(null)}
                  className="h-8 w-8 shrink-0 text-slate-500 md:hidden"
                  aria-label="Volver a la lista"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>

                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarFallback className="bg-slate-200 text-xs font-bold text-slate-600">
                    {inicial(activa?.nombre, abierta)}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-[7rem] flex-1 basis-0">
                  <strong className="block truncate text-[0.92rem] font-semibold text-slate-900">
                    {comoSeLlama(activa) || abierta}
                  </strong>
                  <span className="flex items-center gap-1.5 text-[0.7rem] text-slate-400">
                    {Boolean((activa?.nombre || '').trim()) && (
                      <span className="truncate">{abierta}</span>
                    )}
                    {activa?.necesitaHumano && (
                      <span className="flex shrink-0 items-center gap-1 font-semibold text-red-600">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden="true" />
                        Te está esperando
                      </span>
                    )}
                  </span>
                </div>

                {/* SIEMPRE hay un botón, y dice en qué estado está la
                    conversación. Los dos estados son excluyentes: o la
                    atiende el asistente y la podés agarrar, o es tuya y la
                    podés soltar. Un botón que aparece y desaparece obliga a
                    deducir el estado por su ausencia.

                    Depende de `enManosHumanas`, NO de que te esté esperando.
                    Estuvo atado a lo segundo, y como esa marca se apaga en
                    cuanto contestás, el botón se iba justo cuando hacía falta:
                    quedabas con la conversación en la mano y sin forma de
                    devolverla. */}
                {/* Mientras la acción viaja, el botón late. Es opacidad y no
                    tamaño a propósito: al lado tiene el nombre de la persona, y
                    un botón que se agranda le empuja el texto. Además el texto
                    del botón ya dice qué está pasando ("Devolviendo…"); el
                    latido es para que se note sin leer. */}
                <motion.span
                  {...anima}
                  className="ml-auto shrink-0"
                  animate={(devolviendo || tomando) ? latiendo.animate : { opacity: 1 }}
                  transition={(devolviendo || tomando) ? latiendo.transition : { duration: 0.15 }}
                >
                {activa?.enManosHumanas ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={devolver}
                    disabled={devolviendo}
                    className="h-8 shrink-0 gap-1.5 border-emerald-300 text-[0.82rem] font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    <Bot className="h-3.5 w-3.5" />
                    {devolviendo ? 'Devolviendo…' : 'Que siga el asistente'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={tomar}
                    disabled={tomando}
                    title="El asistente deja de responder hasta que se la devuelvas"
                    className="h-8 shrink-0 gap-1.5 border-blue-200 text-[0.82rem] font-semibold text-blue-700 hover:bg-blue-50"
                  >
                    <Hand className="h-3.5 w-3.5" />
                    {tomando ? 'Tomando…' : 'Tomar el control'}
                  </Button>
                )}
                </motion.span>

                {/* Borrar. Va en un diálogo de confirmación porque es
                    irreversible y no hay papelera; y va al final de la barra,
                    separado, porque no es algo que se haga seguido: pegado a
                    "Tomar el control" se aprieta sin querer. */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      aria-label="Borrar esta conversación"
                      title="Borrar esta conversación"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[26rem]">
                    <DialogHeader>
                      <DialogTitle>¿Borrar esta conversación?</DialogTitle>
                      <DialogDescription>
                        Se borran todos los mensajes con{' '}
                        <strong className="font-semibold text-slate-700">
                          {comoSeLlama(activa) || abierta}
                        </strong>
                        {' '}y no se pueden recuperar. A la persona no le llega
                        nada: esto sólo borra lo que ves acá.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                      <DialogClose asChild>
                        <Button type="button" variant="outline">Cancelar</Button>
                      </DialogClose>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={borrar}
                        disabled={borrando}
                      >
                        {borrando ? 'Borrando…' : 'Borrar para siempre'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* La caja de mensajes es un div con scroll propio y NO un
                  ScrollArea de Radix: el efecto de más arriba le escribe
                  `scrollTop` directo, y con ScrollArea habría que ir a buscar
                  el viewport de adentro. La forma de scrollear es lo único de
                  esta pantalla que ya costó un bug. */}
              <div className="relative min-h-0 flex-1">
                <div
                  ref={cajaDeMensajes}
                  onScroll={alScrollear}
                  className="h-full overflow-y-auto bg-slate-50 px-3 py-4 sm:px-4"
                >
                  {hilo.map((m, i) => {
                    const quien = AUTORES[m.autor] || AUTORES.bot;
                    const previo = hilo[i - 1];
                    const cambioDeDia = diaDe(m.momento) !== diaDe(previo?.momento);

                    // `sistema` no habló: es algo que PASÓ. Centrado y sin
                    // burbuja, porque una burbuja lo haría parecer un mensaje
                    // que el cliente recibió, y el cliente no recibe nada de esto.
                    if (m.autor === 'sistema') {
                      return (
                        <React.Fragment key={m.id}>
                          {cambioDeDia && <CorteDeDia momento={m.momento} />}
                          <p className="mx-auto my-3 w-fit max-w-[85%] rounded-full border border-slate-200 bg-white px-3 py-1 text-center text-[0.72rem] text-slate-500">
                            {m.texto}
                          </p>
                        </React.Fragment>
                      );
                    }

                    // Encadenado: mismo autor que el anterior y mismo día.
                    const agrupado = Boolean(
                      previo
                      && previo.autor === m.autor
                      && previo.autor !== 'sistema'
                      && !cambioDeDia,
                    );

                    return (
                      <React.Fragment key={m.id}>
                        {cambioDeDia && <CorteDeDia momento={m.momento} />}
                        <Mensaje
                          mensaje={m}
                          quien={quien}
                          nombreCliente={inicial(activa?.nombre, abierta)}
                          agrupado={agrupado}
                          animar={animarNuevos}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Acá había un degradado sobre el final de la caja, de los que
                    se usan para insinuar que hay más contenido. Se sacó después
                    de verlo en un celular: la caja es baja, y lo que el
                    degradado desvanecía era el ÚLTIMO mensaje — justo el que
                    uno abre la conversación para leer. Insinuaba lo de arriba
                    borroneando lo de abajo. */}
              </div>

              <form
                onSubmit={enviar}
                className="flex items-end gap-2 border-t border-slate-200 bg-white px-3 py-2.5"
              >
                <textarea
                  value={borrador}
                  onChange={(e) => setBorrador(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter envía, Shift+Enter hace un salto de línea. Es lo
                    // que la gente ya tiene en el dedo de usar WhatsApp.
                    if (e.key === 'Enter' && !e.shiftKey) enviar(e);
                  }}
                  placeholder="Escribí tu respuesta…"
                  rows={2}
                  disabled={enviando}
                  className={cn(
                    'min-h-[46px] flex-1 resize-none rounded-xl border border-slate-300 px-3.5 py-2.5',
                    'text-[0.88rem] leading-snug text-slate-800 outline-none',
                    'placeholder:text-slate-400',
                    'focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20',
                    'disabled:bg-slate-50 disabled:text-slate-400',
                  )}
                />
                <motion.span
                  {...anima}
                  className="shrink-0"
                  animate={enviando ? latiendo.animate : { opacity: 1 }}
                  transition={enviando ? latiendo.transition : { duration: 0.15 }}
                >
                <Button
                  type="submit"
                  disabled={enviando || !borrador.trim()}
                  className={cn(
                    'h-[46px] shrink-0 gap-1.5 rounded-xl bg-emerald-600 px-4 text-[0.85rem]',
                    'font-semibold text-white hover:bg-emerald-700',
                    'disabled:bg-slate-200 disabled:text-slate-400',
                  )}
                >
                  <Send className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {enviando ? 'Enviando…' : 'Enviar'}
                  </span>
                </Button>
                </motion.span>
              </form>

              <p className="m-0 border-t border-slate-100 bg-white px-3 py-1.5 text-[0.7rem] text-slate-400">
                Le llega por WhatsApp desde el mismo número del asistente.
              </p>
            </motion.div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ConversacionesWhatsApp;
